/**
 * Camera / occlusion DEBUG panel — a dev tuning surface for dialling in the locked
 * perspective by eye (Aviv, 2026-06-29). Toggle with the backtick/grave key (`).
 *
 * Exposes live sliders for the levers that drive "how the perspective feels":
 *   • Pitch  — camera elevation above the ground plane (deg). Higher = more
 *              top-down (less tree/rock occlusion); lower = more oblique/cinematic.
 *   • FOV    — perspective field of view (deg). Narrower = flatter / more isometric
 *              (LoL is 40); wider = more wide-angle distortion (we shipped 50).
 *   • Follow / zoom smoothing — exponential damping time constants for the
 *              MOBA follow-cam and wheel-zoom (see main.ts's dampAlpha()).
 *
 * Values persist to localStorage so a tuning session survives reloads, and are
 * re-applied on the next boot via applyStored(). This is a tuning aid, NOT the
 * shipping config — once a look is chosen we bake the numbers into main.ts /
 * toon.ts and can delete the panel.
 */

export interface CameraDebugApi {
  /** Camera elevation above the horizon, in degrees (90 = straight top-down). */
  setPitchDeg(deg: number): void;
  /**
   * Locked view direction (azimuth), in degrees. 0 = camera south of the hero
   * looking north; shipped default = 0 (main.ts FIXED_YAW — re-picked after
   * the 2026-07-05 N-S map flip; the old 78 was taste on the mirrored map);
   * positive rotates the world clockwise on screen. Player
   * rotation stays disabled — this re-aims the fixed perspective.
   */
  setYawDeg(deg: number): void;
  /** Perspective vertical FOV, in degrees. */
  setFovDeg(deg: number): void;
  /** Master on/off for the whole-canopy occluder fade. */
  setCanopyFadeEnabled(v: boolean): void;
  /**
   * Follow-camera damping time constant, in milliseconds (exponential decay:
   * alpha = 1 - exp(-dt/tau)). Lower = snappier/tighter follow; higher = more
   * floaty. 2026-07-02, camera-follow refinement.
   */
  setFollowTauMs(ms: number): void;
  /**
   * Wheel-zoom damping time constant, in milliseconds (same exponential-decay
   * shape as follow). Lower = snappier zoom; higher = floatier.
   * 2026-07-02, zoom smoothing.
   */
  setZoomTauMs(ms: number): void;
  /** Current shipped/default values, for slider initialisation. */
  defaults: {
    pitchDeg: number; yawDeg: number; fovDeg: number;
    canopyFadeEnabled: boolean;
    followTauMs: number; zoomTauMs: number;
  };
}

const STORAGE_KEY = "tribez.cameraDebug";

type Stored = {
  pitchDeg?: number; yawDeg?: number; fovDeg?: number;
  canopyFadeEnabled?: boolean;
  followTauMs?: number; zoomTauMs?: number;
  /** One-shot migration marker — see load(). */
  mapFlipV1?: boolean;
};

function load(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const s = raw ? (JSON.parse(raw) as Stored) : {};
    // One-shot migration (2026-07-05 N-S map flip): any persisted yaw was
    // tuned on the old MIRRORED map and re-applying it on the flipped map
    // silently overrides the new 0° default. Drop it once.
    if (!s.mapFlipV1) {
      delete s.yawDeg;
      s.mapFlipV1 = true;
      save(s);
    }
    return s;
  } catch {
    return {};
  }
}

function save(s: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota — keep in-memory only */
  }
}

/**
 * Apply any stored overrides through the API. Call once after the match-start
 * camera setup so a tuned pitch/FOV/cutout survives a page reload.
 */
export function applyStoredCameraDebug(api: CameraDebugApi): void {
  const s = load();
  if (s.pitchDeg !== undefined) api.setPitchDeg(s.pitchDeg);
  if (s.yawDeg !== undefined) api.setYawDeg(s.yawDeg);
  if (s.fovDeg !== undefined) api.setFovDeg(s.fovDeg);
  if (s.canopyFadeEnabled !== undefined) api.setCanopyFadeEnabled(s.canopyFadeEnabled);
  if (s.followTauMs !== undefined) api.setFollowTauMs(s.followTauMs);
  if (s.zoomTauMs !== undefined) api.setZoomTauMs(s.zoomTauMs);
}

type SliderKey = "pitchDeg" | "yawDeg" | "fovDeg" | "followTauMs" | "zoomTauMs";
type ToggleKey = "canopyFadeEnabled";

interface Row {
  key: SliderKey;
  label: string;
  min: number;
  max: number;
  step: number;
  get def(): number;
  apply: (v: number) => void;
  fmt: (v: number) => string;
}

export interface CameraDebugPanel {
  toggle(): void;
  destroy(): void;
}

export function createCameraDebug(api: CameraDebugApi): CameraDebugPanel {
  const stored = load();

  const rows: Row[] = [
    {
      key: "followTauMs", label: "Follow smoothing (ms)", min: 30, max: 250, step: 5,
      get def() { return api.defaults.followTauMs; },
      apply: (v) => api.setFollowTauMs(v), fmt: (v) => `${v.toFixed(0)}ms`,
    },
    {
      key: "zoomTauMs", label: "Zoom smoothing (ms)", min: 30, max: 250, step: 5,
      get def() { return api.defaults.zoomTauMs; },
      apply: (v) => api.setZoomTauMs(v), fmt: (v) => `${v.toFixed(0)}ms`,
    },
    {
      key: "pitchDeg", label: "Pitch (elev°)", min: 15, max: 80, step: 1,
      get def() { return api.defaults.pitchDeg; },
      apply: (v) => api.setPitchDeg(v), fmt: (v) => `${v.toFixed(0)}°`,
    },
    {
      key: "yawDeg", label: "Yaw (rotate°)", min: -180, max: 180, step: 1,
      get def() { return api.defaults.yawDeg; },
      apply: (v) => api.setYawDeg(v), fmt: (v) => `${v.toFixed(0)}°`,
    },
    {
      key: "fovDeg", label: "FOV°", min: 28, max: 65, step: 1,
      get def() { return api.defaults.fovDeg; },
      apply: (v) => api.setFovDeg(v), fmt: (v) => `${v.toFixed(0)}°`,
    },
  ];

  const panel = document.createElement("div");
  panel.id = "camera-debug";
  panel.style.cssText = [
    "position:fixed", "top:120px", "left:12px", "z-index:9999",
    "background:rgba(20,16,12,0.92)", "border:1px solid #5a4a36", "border-radius:8px",
    "padding:10px 12px", "min-width:230px", "display:none",
    "font:12px/1.4 ui-monospace,Menlo,Consolas,monospace", "color:#f0e6d6",
    "box-shadow:0 6px 20px rgba(0,0,0,0.5)", "user-select:none",
  ].join(";");

  const title = document.createElement("div");
  title.textContent = "📷 camera / occlusion debug  (`)";
  title.style.cssText = "font-weight:700;color:#e0a64a;margin-bottom:8px;letter-spacing:.3px";
  panel.appendChild(title);

  const current: Stored = {};

  for (const row of rows) {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin:7px 0";

    const head = document.createElement("div");
    head.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px";
    const lab = document.createElement("span");
    lab.textContent = row.label;
    lab.style.color = "#b29c82";
    const val = document.createElement("span");
    val.style.cssText = "color:#5aa6d0;font-weight:700";
    head.appendChild(lab); head.appendChild(val);

    const slider = document.createElement("input");
    slider.type = "range";
    slider.min = String(row.min); slider.max = String(row.max); slider.step = String(row.step);
    slider.style.cssText = "width:100%;accent-color:#e0a64a;cursor:pointer";

    const initial = stored[row.key] ?? row.def;
    slider.value = String(initial);
    val.textContent = row.fmt(initial);
    current[row.key] = initial;

    slider.addEventListener("input", () => {
      const v = Number(slider.value);
      val.textContent = row.fmt(v);
      row.apply(v);
      current[row.key] = v;
      save(current);
    });
    // Stop game keybinds (Q/W/E/R etc.) from firing while dragging the panel.
    slider.addEventListener("keydown", (e) => e.stopPropagation());

    wrap.appendChild(head); wrap.appendChild(slider);
    panel.appendChild(wrap);
  }

  // Whole-canopy fade before-after toggle (2026-07-02). Plain checkbox — the
  // slider `Row` shape above doesn't fit a boolean control.
  type ToggleRow = { key: ToggleKey; label: string; get def(): boolean; apply: (v: boolean) => void };
  const toggleRows: ToggleRow[] = [
    {
      key: "canopyFadeEnabled", label: "Canopy fade (whole doodad)",
      get def() { return api.defaults.canopyFadeEnabled; },
      apply: (v) => api.setCanopyFadeEnabled(v),
    },
  ];
  for (const row of toggleRows) {
    const wrap = document.createElement("label");
    wrap.style.cssText = "display:flex;align-items:center;gap:6px;margin:7px 0;cursor:pointer";
    const box = document.createElement("input");
    box.type = "checkbox";
    const initial = stored[row.key] ?? row.def;
    box.checked = initial;
    current[row.key] = initial;
    row.apply(box.checked);
    const lab = document.createElement("span");
    lab.textContent = row.label;
    lab.style.color = "#b29c82";
    box.addEventListener("change", () => {
      row.apply(box.checked);
      current[row.key] = box.checked;
      save(current);
    });
    wrap.appendChild(box); wrap.appendChild(lab);
    panel.appendChild(wrap);
  }

  const reset = document.createElement("button");
  reset.textContent = "reset to shipped defaults";
  reset.style.cssText =
    "margin-top:8px;width:100%;padding:5px;background:#2a2016;color:#b29c82;" +
    "border:1px solid #5a4a36;border-radius:5px;cursor:pointer;font:inherit";
  reset.addEventListener("click", () => {
    save({});
    for (const row of rows) row.apply(row.def);
    // Re-sync sliders to defaults.
    const sliders = panel.querySelectorAll("input[type=range]");
    rows.forEach((row, i) => {
      const s = sliders[i] as HTMLInputElement | undefined;
      if (s) { s.value = String(row.def); s.dispatchEvent(new Event("input")); }
    });
    // Re-sync toggle checkboxes to defaults.
    const checks = panel.querySelectorAll("input[type=checkbox]");
    toggleRows.forEach((row, i) => {
      const c = checks[i] as HTMLInputElement | undefined;
      if (c) { c.checked = row.def; c.dispatchEvent(new Event("change")); }
    });
    save({});
  });
  panel.appendChild(reset);

  document.body.appendChild(panel);

  let visible = false;
  function toggle(): void {
    visible = !visible;
    panel.style.display = visible ? "block" : "none";
  }
  function destroy(): void {
    panel.remove();
  }

  return { toggle, destroy };
}
